import React, { useState, useEffect, useCallback } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Login com Google — configuracao da instalacao (so' o super; fica na empresa 1).
 * Porte de 73390a6f SEM o "revelar": o Client Secret nunca volta ao navegador — a tela
 * so' sabe se ha' um salvo (hasSecret). Campo vazio ao salvar = mantem o atual.
 */
const PROBLEMS = {
  sem_credenciais: "Informe o Client ID e o Client Secret.",
  client_id_invalido: "Client ID fora do formato do Google (...apps.googleusercontent.com).",
  callback_invalido: "URI de redirecionamento inválida (BACKEND_URL do servidor ausente?).",
  frontend_url_ausente: "FRONTEND_URL do servidor ausente ou inválido.",
  segredo_ilegivel: "O Client Secret salvo não pode ser lido (JWT_SECRET mudou?). Salve de novo.",
  erro_leitura: "Não foi possível ler a configuração.",
};

const GoogleAuthConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [autoLink, setAutoLink] = useState(false);

  const apply = useCallback((data) => {
    setCfg(data || null);
    setClientId(data?.clientId || "");
    setCallbackUrl(data?.callbackUrlCustom || "");
    setAutoLink(data?.autoLink === true);
    setClientSecret("");
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/google-config");
      apply(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (payload, okMsg) => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/google-config", payload);
      apply(data);
      toast.success(okMsg);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const payload = { clientId: clientId.trim(), callbackUrl: callbackUrl.trim(), autoLink };
    if (clientSecret.trim()) payload.clientSecret = clientSecret.trim();
    save(payload, "Configuração do login com Google salva.");
  };

  const handleDisable = () => {
    save({ clientId: "", autoLink: false }, "Login com Google desligado.");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 16 }}>
        <CircularProgress size={22} />
      </div>
    );
  }

  const statusChip = cfg?.enabled ? (
    <Chip size="small" label={cfg.source === "env" ? "Ligado (.env do servidor)" : "Ligado"} style={{ backgroundColor: "#0FA95822", color: "#0FA958", fontWeight: 700 }} />
  ) : (
    <Chip size="small" label="Desligado" style={{ backgroundColor: "#9E9E9E22", color: "#9E9E9E", fontWeight: 700 }} />
  );

  return (
    <Paper elevation={0} style={{ border: "1px solid rgba(128,128,128,0.28)", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <Typography style={{ fontSize: "0.8rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", opacity: 0.8 }}>
          Login com Google
        </Typography>
        {statusChip}
      </div>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 10 }}>
        Credenciais OAuth 2.0 (tipo "Aplicativo da Web") do Google Cloud Console. O Client Secret é
        guardado cifrado e nunca é mostrado de volta. Sem Client ID e Secret o botão "Entrar com
        Google" não aparece e nada muda no login por senha.
      </Typography>
      {!cfg?.enabled && cfg?.problem && PROBLEMS[cfg.problem] && cfg.problem !== "sem_credenciais" && (
        <Typography variant="body2" style={{ color: "#F5A623", marginBottom: 10 }}>
          {PROBLEMS[cfg.problem]}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client ID"
            variant="outlined"
            size="small"
            fullWidth
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="xxxxx.apps.googleusercontent.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client Secret"
            variant="outlined"
            size="small"
            fullWidth
            type="password"
            autoComplete="new-password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={cfg?.hasSecret ? "•••••••• configurado — digite só para trocar" : "GOCSPX-..."}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="URI de redirecionamento (opcional)"
            variant="outlined"
            size="small"
            fullWidth
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            placeholder={cfg?.callbackUrlDefault || "https://api.seudominio.com/auth/google/callback"}
            helperText={
              `Cadastre no Google exatamente: ${cfg?.callbackUrl || cfg?.callbackUrlDefault || "(defina BACKEND_URL no servidor)"}`
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={autoLink}
                onChange={(e) => setAutoLink(e.target.checked)}
              />
            }
            label="Vincular automaticamente pelo e-mail no primeiro login (nunca vale para super)"
          />
          <Typography variant="caption" color="textSecondary" display="block">
            Desligado (recomendado): cada usuário entra com a senha e clica em "Vincular conta Google" no
            próprio perfil. Ligue só se os e-mails de cadastro forem caixas reais da empresa — com e-mail
            fictício, quem controlar aquele endereço entraria no usuário.
          </Typography>
        </Grid>
        <Grid item xs={12} style={{ display: "flex", gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={saving}
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={14} /> : null}
          >
            Salvar
          </Button>
          {(cfg?.hasSecret || cfg?.clientId) && cfg?.source !== "env" && (
            <Button variant="outlined" size="small" disabled={saving} onClick={handleDisable}>
              Desligar
            </Button>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GoogleAuthConfig;
